import {
    clearAllBodyScrollLocks,
    disableBodyScroll,
    enableBodyScroll,
} from "body-scroll-lock";
import classNames from "classnames";
import React, { type JSX } from "react";
import { CSSTransition } from "react-transition-group";
import { Key } from "ts-key-enum";

import { getDocument, getWindow } from "@thelabnyc/thelabui/src/utils/dom";

import { MOBILE_WIDTH_BODY_SCROLL } from "../apps/configurator/constants";
import { ConfiguratorPanel } from "../apps/configurator/elements/ConfiguratorPanel";
import {
    ConfiguratorPanelTrigger,
    disableMainConfiguratorScrolling,
} from "../apps/configurator/elements/ConfiguratorPanelTrigger";
import { ConfiguratorTypes } from "../constants";
import {
    IOptionCode,
    IOptionInputType,
    IOptionSingleValue,
    IProduct,
} from "../models/catalogue";
import { getOptionValueHash } from "../utils/catalogue";
import { usePageSetting } from "../utils/settings";
import { listPossibleOptionValues } from "../utils/sorting";
import { LevelSelectionPanel } from "./LevelSelectionPanel";
import { ProductOptionClassicSelector as ClassicSelector } from "./ProductOptionClassicSelector";
import { ProductOptionColorSelector as ColorSelector } from "./ProductOptionColorSelector";
import { ProductOptionDropdownSelector as DropdownSelector } from "./ProductOptionDropdownSelector";
import { ProductOptionRadioSelector as RadioSelector } from "./ProductOptionRadioSelector";

import styles from "./ProductOptionSelector.module.scss";

function getClassesNames(
    configuratorType: ConfiguratorTypes | undefined,
    style: "full" | "mini" | undefined,
    inputType: string,
    prefixName: string,
    options: IOptionSingleValue[],
): {
    configuratorClass: string;
    prefixClass: string;
    selectClass: string;
} {
    const hasSingleOption = options.length === 1;
    const configuratorClass = classNames({
        "configurator__select-container": true,
        [`configurator__select-container--${configuratorType}`]: true,
        "configurator__select-container--one-option": hasSingleOption,
        [`configurator__select-container--${style}`]: true,
        [`configurator__select-container--${prefixName}`]: true,
        [`configurator__select-container--${inputType}`]: true,
        [styles.panelContainer]: configuratorType === ConfiguratorTypes.PANEL,
    });
    const prefixClass = classNames({
        "configurator__prefix": true,
        [`configurator__prefix--${configuratorType}`]: true,
        "configurator__prefix--one-option": hasSingleOption,
        [`configurator__prefix--${prefixName}`]: true,
    });
    const selectClass = classNames({
        "configurator__select": true,
        "configurator__select--one-option": hasSingleOption,
        [`configurator__select--${configuratorType}`]: true,
        [`configurator__select--${prefixName}`]: true,
    });
    return {
        configuratorClass,
        prefixClass,
        selectClass,
    };
}

function getLevelMarketingCopy(
    option: string | undefined,
    value: IOptionSingleValue | undefined,
    levelMarketingCopy: Record<string, { main_copy: string }> | null,
): string {
    if (option && levelMarketingCopy && levelMarketingCopy[option]) {
        return levelMarketingCopy[option].main_copy;
    }
    return value ? getOptionValueHash(value) : "";
}

function handleBodyScrollLock(
    panelContainer: HTMLDivElement | null,
    isPanelOpen: boolean,
): void {
    if (!panelContainer) {
        return;
    }

    const window = getWindow();
    if (!window) return;

    if (isPanelOpen) {
        if (window.innerWidth < MOBILE_WIDTH_BODY_SCROLL) {
            disableBodyScroll(panelContainer);
        } else {
            enableBodyScroll(panelContainer);
        }
    }
}

function closePanel(panelContainer: HTMLDivElement | null): void {
    const document = getDocument();
    if (!document) return;

    const container =
        document.querySelector<HTMLElement>(".pdp-hero .configurator") ??
        document.querySelector<HTMLElement>(".product-hero__configurator");
    if (container) {
        container.classList.remove("overflow-hidden");
    }

    document.body.style.overflowX = "hidden";
    setTimeout(() => {
        document.body.classList.remove("configurator-panel-open");
    }, 500);

    // `enableBodyScroll` should run before setState `isPanelOpen: false`,
    // so inline style `position: fixed` from `<body>` will be removed first before removing `.configurator-panel-open` from `<body>`.
    // This way can avoid the issue of the page jumping to the top when panel is opened/closed
    if (panelContainer) {
        enableBodyScroll(panelContainer);
    }
}

export const ProductOptionSelector = (props: {
    selectorID: string;
    rootProduct: IProduct | null;
    variant: IProduct | null;
    code: IOptionCode;
    value: IOptionSingleValue | undefined;
    onOptionChange: (
        namespace: string,
        code: IOptionCode,
        value: IOptionSingleValue,
    ) => void;
    configuratorType?: ConfiguratorTypes;
    style?: "full" | "mini";
    chatLinkComponent?: JSX.Element | null;
    liveChatHeader?: string;
    enableChat?: boolean;
    isPlpPanelStyle?: boolean;
}): React.ReactNode => {
    const style = props.style ?? "full";
    const enableChat = props.enableChat ?? false;
    const panelContainerRef = React.useRef<HTMLDivElement>(null);
    const changeLockRef = React.useRef<boolean>(false);
    const prevValueRef = React.useRef<IOptionSingleValue | undefined>(
        props.value,
    );

    const [isPanelOpen, setIsPanelOpen] = React.useState(false);
    const [isEnterKeyDown, setIsEnterKeyDown] = React.useState(false);
    const [isClicked, setIsClicked] = React.useState(false);

    const levelMarketingCopy = usePageSetting("level_marketing_copy");

    // componentWillUnmount cleanup
    React.useEffect(() => {
        return () => {
            clearAllBodyScrollLocks();
        };
    }, []);

    // Handle body scroll lock when panel opens
    React.useEffect(() => {
        handleBodyScrollLock(panelContainerRef.current, isPanelOpen);
    }, [isPanelOpen]);

    // componentDidUpdate logic - close panel on value change or interaction
    React.useEffect(() => {
        if (props.configuratorType !== ConfiguratorTypes.PANEL) {
            return;
        }
        if (changeLockRef.current) {
            return;
        }

        // Don't close the panel if we're inside the special
        // Sealy "Level Selection Panel"
        if (props.code !== "option_level") {
            const valueChanged = prevValueRef.current !== props.value;
            if (valueChanged || isEnterKeyDown || isClicked) {
                handleClosePanel();
            }
        }

        prevValueRef.current = props.value;
    }, [
        props.value,
        props.code,
        props.configuratorType,
        isEnterKeyDown,
        isClicked,
    ]);

    const handleOptionChange = (
        e: React.FormEvent<HTMLSelectElement | HTMLInputElement>,
    ): void => {
        // Preventing default actions results in weird behavior with keyboard navigation
        const namespace = props.rootProduct
            ? props.rootProduct.product_class_slug
            : "";
        props.onOptionChange(namespace, props.code, e.currentTarget.value);
    };

    const handleClosePanel = (): void => {
        closePanel(panelContainerRef.current);

        // TOOD: Remove the panel after the close animation is completed. Tie the animation variables into the component here
        setIsPanelOpen(false);
        setIsEnterKeyDown(false);
        setIsClicked(false);
    };

    const handleOpenPanel = (): void => {
        disableMainConfiguratorScrolling();
        setIsPanelOpen(true);
    };

    const handleKeyboardDown = (
        e: React.KeyboardEvent<HTMLInputElement>,
    ): void => {
        if (props.configuratorType !== ConfiguratorTypes.PANEL) {
            return;
        }
        changeLockRef.current = true;
        if (e.key === Key.Enter) {
            changeLockRef.current = false;
            setIsEnterKeyDown(true);
        }
    };

    const handleClick = (): void => {
        if (props.configuratorType !== ConfiguratorTypes.PANEL) {
            return;
        }

        setIsEnterKeyDown(false);
        setIsClicked(true);
    };

    if (!props.rootProduct) {
        return null;
    }

    const inputTypes =
        props.rootProduct.attributes.product_option_input_types?.value || {};
    const inputType: IOptionInputType = inputTypes[props.code] || "dropdown";

    const prefixAttr = props.variant
        ? props.variant.attributes[props.code]
        : "";
    const prefixName = prefixAttr
        ? prefixAttr.name.toLowerCase()
        : props.code.substr(props.code.indexOf("_") + 1, props.code.length);
    let prefixNameCapped = prefixName[0].toUpperCase() + prefixName.substr(1);

    // Hardcode custom string override for tsi-sealy (Refs #21451)
    // 'Level' becomes 'Sealy® Technology'
    if (prefixName === "level") {
        prefixNameCapped = "Sealy® Technology";
    }

    const options = listPossibleOptionValues(props.code, props.rootProduct);

    const { configuratorClass, prefixClass, selectClass } = getClassesNames(
        props.configuratorType,
        style,
        inputType,
        prefixName,
        options,
    );
    const isPanel = props.configuratorType === ConfiguratorTypes.PANEL;

    const selectorProps = {
        configuratorType: props.configuratorType,
        selectorID: props.selectorID,
        rootProduct: props.rootProduct,
        variant: props.variant,
        code: props.code,
        value: props.value,
        options: options,
        prefixNameCapped: prefixNameCapped,
        configuratorClass: configuratorClass,
        prefixClass: prefixClass,
        selectClass: selectClass,
        onChange: handleOptionChange,
        onKeyboardDown: handleKeyboardDown,
        onClick: handleClick,
        onClosePanel: handleClosePanel,
        isPlpPanelStyle: props.isPlpPanelStyle,
    };

    const isClassic = props.configuratorType === ConfiguratorTypes.CLASSIC;
    const isMini = style === "mini";
    const showClassicStyle = isClassic || isMini;

    // Show a panel?
    if (isPanel) {
        const isOptionLevel = selectorProps.code === "option_level";
        return (
            <div>
                <CSSTransition
                    in={isPanelOpen}
                    timeout={500}
                    classNames="configurator__panel-"
                    unmountOnExit={true}
                    nodeRef={panelContainerRef}
                >
                    <div ref={panelContainerRef}>
                        <ConfiguratorPanel
                            chatLinkComponent={props.chatLinkComponent}
                            liveChatHeader={props.liveChatHeader}
                            panelClass={"option"}
                            enableChat={enableChat}
                            radioSelector={
                                isOptionLevel ? (
                                    <LevelSelectionPanel
                                        isIcon={true}
                                        {...selectorProps}
                                        showPricing={
                                            inputType === "radio-with-pricing"
                                        }
                                    />
                                ) : (
                                    <RadioSelector
                                        isIcon={true}
                                        {...selectorProps}
                                        showPricing={
                                            inputType === "radio-with-pricing"
                                        }
                                    />
                                )
                            }
                            {...selectorProps}
                        />
                    </div>
                </CSSTransition>
                <ConfiguratorPanelTrigger
                    prefixNameCapped={prefixNameCapped}
                    onOpenPanel={handleOpenPanel}
                >
                    {isOptionLevel
                        ? getLevelMarketingCopy(
                              props.value,
                              props.value,
                              levelMarketingCopy,
                          )
                        : props.value
                          ? getOptionValueHash(props.value)
                          : ""}
                </ConfiguratorPanelTrigger>
            </div>
        );
    }

    // Show the color selector style?
    if (showClassicStyle && prefixName === "color") {
        return <ColorSelector {...selectorProps} />;
    }

    // Show the classic selector style?
    if (showClassicStyle) {
        return <ClassicSelector {...selectorProps} />;
    }

    // Show the dropdown selector style?
    if (inputType === "dropdown" || inputType === "dropdown-with-pricing") {
        return (
            <DropdownSelector
                {...selectorProps}
                showPricing={inputType === "dropdown-with-pricing"}
            />
        );
    }

    // Default to the the radio-button selector style
    return <RadioSelector {...selectorProps} />;
};
